import { useInfiniteQuery, UseInfiniteQueryResult, useQuery } from "@tanstack/react-query";

import useSystemStore from "@store/useSystemStore";
import userService from "@services/user.service";
import {
  USERS_QUERY_KEY,
  QUERY_CACHE_DEFAULT_EXPIRATION_MS,
  USER_QUERY_KEY,
  QUERY_CACHE_LONG_EXPIRATION_MS,
} from "@config/query";
import { User } from "@app-types/common.type";

export function useUsers(enabled: boolean = true) {
  const { isAppActive } = useSystemStore();

  const params = new URLSearchParams();
  params.append("limit", "500");

  const { data, error, isLoading, fetchStatus, isPlaceholderData, refetch } = useQuery({
    enabled: enabled && isAppActive,
    queryKey: [USERS_QUERY_KEY],
    queryFn: async () => {
      return await userService.getAllUsers(params);
    },
    staleTime: QUERY_CACHE_DEFAULT_EXPIRATION_MS,
    retry: false,
  });

  return { data, error, isLoading, fetchStatus, isPlaceholderData, refetch };
}

export function useAllUsersInfinite(
  searchQuery: string = "",
  includePending: boolean = false,
  customParams: { initial?: number; limit?: number } = {}
): UseInfiniteQueryResult<User[], Error> {
  const fetchUsers = async ({ pageParam = { limit: customParams.initial ?? 50, offset: 0 } }) => {
    const params = new URLSearchParams();
    if (includePending) params.append("include_pending", "true");

    if (searchQuery) {
      params.append("search", searchQuery);
      params.append("ordering", "full_name");
      // Skip limit/offset when searching to get all results
    } else {
      params.append("limit", pageParam.limit.toString());
      params.append("offset", pageParam.offset.toString());
    }

    return await userService.getAllUsers(params);
  };

  return useInfiniteQuery({
    queryKey: [`${USER_QUERY_KEY}`, searchQuery, includePending, customParams],
    queryFn: fetchUsers,
    getNextPageParam: (lastPage) => {
      // All results fetched in a single request when searching
      if (searchQuery) return undefined;

      if (customParams.limit !== undefined) {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          const limit = customParams.limit;
          const offset = Number(url.searchParams.get("offset")) || 0;
          return { limit, offset };
        }
        return undefined;
      }
      if (lastPage.next) {
        const url = new URL(lastPage.next);
        const limit = Number(url.searchParams.get("limit")) || 10;
        const offset = Number(url.searchParams.get("offset")) || 0;
        return { limit, offset };
      }
      return undefined;
    },
    select: (data) => {
      return data.pages.flatMap((page) =>
        page.results.map((user) => ({
          ...user,
          full_name: user.full_name,
        }))
      );
    },
    staleTime: QUERY_CACHE_LONG_EXPIRATION_MS,
    initialPageParam: { limit: customParams.initial ?? 50, offset: 0 },
  });
}
